import { useEffect, useState, useRef } from 'react';
import { useDataFetchIndicator } from '@/hooks';
import { useBranding } from '@/providers/Branding.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useGenerateEmbedUrlDisputeManagement from '@/hooks/useApi/useGenerateEmbedUrlDisputeManagement';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { DisputeManagementIFrameEvent, DisputeManagementIFrameMethod } from 'vopay';

export default function DisputeManagementPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [, setLoading] = useDataFetchIndicator();
  const { primaryColor } = useBranding();

  const {
    userSettings: { timezone },
  } = useUserSettingsProvider();

  const [isInitialized, setIsInitialized] = useState(false);
  const { link, generateEmbedUrl } = useGenerateEmbedUrlDisputeManagement();
  const alertDialog = useAlertDialog();

  const {
    response: { AccountID },
  } = useClientData();

  useEffect(() => {
    if (AccountID) {
      setLoading(true);
      init();
    }
  }, [AccountID]);

  useEffect(() => {
    const controller = new AbortController();

    window.addEventListener(
      'message',
      (event: MessageEvent<DisputeManagementIFrameEvent>) => {
        if (!event.data) return;

        const { type } = event.data;

        if (type === 'LOADING') {
          setLoading(!event.data.payload.completed);
        }

        if (type === 'INITIALIZED') {
          postMessageToIframe({
            type: 'SET_CONFIG',
            payload: {
              primaryColor,
              timezone,
            },
          });

          setLoading(false);
        }

        if (type === 'READY') {
          setIsInitialized(true);
        }
      },
      {
        signal: controller.signal,
      },
    );

    return () => {
      controller.abort();
    };
  }, []);

  function postMessageToIframe(message: DisputeManagementIFrameMethod) {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage(message, '*');
    }
  }

  async function init() {
    try {
      const res = await generateEmbedUrl({
        AccountID,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        alertDialog.error('Something went wrong', err.message);
      }
    }
  }

  return (
    <>
      {link && (
        <iframe
          ref={iframeRef}
          src={link}
          title="Dispute Management Portal"
          style={{ width: '100%', height: '100%', border: 'none', visibility: isInitialized ? 'visible' : 'hidden' }}
          allow="clipboard-write"
          seamless
        ></iframe>
      )}
    </>
  );
}
